import { Link } from "react-router-dom";
import { ArrowLeft, Shield, FileText, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import Footer from "@/components/marketing/Footer";
import SEO from "@/components/marketing/SEO";

const Legal = () => {
  return (
    <>
      <SEO
        title="Mentions légales et politique de confidentialité"
        description="Conditions générales d'utilisation et politique de gestion des données personnelles de Piktable. Solution développée par Reversed Sàrl à Lausanne, Suisse."
        canonical="/legal"
      />
      <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="container mx-auto px-4 py-4">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-playfair font-bold mb-4">
            Informations légales
          </h1>
          <p className="text-muted-foreground text-lg">
            Dernière mise à jour : 10 novembre 2025
          </p>
        </div>

        {/* Table of Contents */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Sommaire
            </CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="flex flex-col gap-2">
              <a href="#cgu" className="text-primary hover:underline">
                → Conditions générales d'utilisation
              </a>
              <a href="#privacy" className="text-primary hover:underline">
                → Politique de gestion des données personnelles
              </a>
            </nav>
          </CardContent>
        </Card>

        {/* CGU Section */}
        <section id="cgu" className="mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl font-playfair">
                Conditions générales d'utilisation – Piktable
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <div className="space-y-6">
                {/* Article 1 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">1. Objet</h2>
                  <p className="mb-2">
                    <strong>1.1</strong> Les présentes conditions générales (les « Conditions ») régissent l'utilisation et la fourniture de la solution de réservation en ligne Piktable (la « Solution »), éditée et exploitée par Reversed Sàrl, sise Chemin des Fleurettes 22, 1007 Lausanne (Suisse).
                  </p>
                  <p className="mb-2">
                    <strong>1.2</strong> La Solution permet aux cafés et restaurants de gérer leurs réservations, tables, créneaux horaires, notes clients et intolérances alimentaires, et d'envoyer des e-mails automatiques de confirmation, modification ou annulation.
                  </p>
                  <p>
                    <strong>1.3</strong> En s'inscrivant à la Solution, le Client reconnaît avoir pris connaissance des présentes Conditions et les accepter sans réserve.
                  </p>
                </div>

                <Separator />

                {/* Article 2 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">2. Définition des services</h2>
                  <p className="mb-2">
                    <strong>2.1</strong> Reversed Sàrl fournit un accès en ligne à Piktable en mode SaaS.
                  </p>
                  <p className="mb-2">
                    <strong>2.2</strong> Le Client obtient un droit non exclusif et non transférable d'utiliser la Solution pendant la durée du contrat.
                  </p>
                  <p className="mb-2">
                    <strong>2.3</strong> Les services incluent les modules indiqués dans la formule choisie selon la grille tarifaire en vigueur (Offre Standard, Offre Printemps 2026) et les éventuelles options souscrites (ex. site internet relié, accompagnement).
                  </p>
                  <p>
                    <strong>2.4</strong> Tout service complémentaire non inclus dans l'offre pourra faire l'objet d'un devis séparé.
                  </p>
                </div>

                <Separator />

                {/* Article 3 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">3. Souscription et facturation</h2>
                  <p className="mb-2">
                    <strong>3.1</strong> Le Client sélectionne une formule (Offre Standard, Offre Printemps 2026) selon la grille tarifaire en vigueur.
                  </p>
                  <p className="mb-2">
                    <strong>3.2</strong> Les tarifs incluent un coût fixe mensuel ou annuel et, selon le plan, un coût variable par réservation.
                  </p>
                  <p className="mb-2">
                    <strong>3.3</strong> Les paiements s'effectuent mensuellement ou annuellement à l'avance. En cas de retard de paiement, Reversed Sàrl se réserve le droit de suspendre temporairement l'accès à la Solution après mise en demeure.
                  </p>
                  <p>
                    <strong>3.4</strong> Les tarifs peuvent être révisés à tout moment avec un préavis de 30 jours. Le Client sera libre de résilier le contrat avant l'entrée en vigueur des nouveaux tarifs.
                  </p>
                </div>

                <Separator />

                {/* Article 4 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">4. Durée et résiliation</h2>
                  <p className="mb-2">
                    <strong>4.1</strong> Le contrat est conclu pour la durée choisie lors de la souscription (mensuelle ou annuelle).
                  </p>
                  <p className="mb-2">
                    <strong>4.2</strong> À l'échéance, il est renouvelé automatiquement pour la même durée, sauf résiliation écrite par l'une des parties au moins 15 jours avant la fin de la période en cours.
                  </p>
                  <p className="mb-2">
                    <strong>4.3</strong> Le Client peut résilier immédiatement en cas de manquement grave de Reversed Sàrl (non-exécution du service).
                  </p>
                  <p>
                    <strong>4.4</strong> Reversed Sàrl peut résilier sans préavis en cas d'utilisation abusive, de non-paiement persistant, ou de violation manifeste des présentes Conditions.
                  </p>
                </div>

                <Separator />

                {/* Article 5 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">5. Obligations du Client</h2>
                  <p className="mb-2">
                    <strong>5.1</strong> Le Client s'engage à utiliser la Solution dans le respect des lois applicables en Suisse et à garantir la confidentialité de ses identifiants.
                  </p>
                  <p className="mb-2">
                    <strong>5.2</strong> Le Client est seul responsable des contenus et informations qu'il saisit (ex. données clients, intolérances, notes).
                  </p>
                  <p className="mb-2">
                    <strong>5.3</strong> Le Client garantit que les données communiquées ne violent pas les droits de tiers et qu'il dispose de la base légale nécessaire pour les transmettre à Reversed Sàrl.
                  </p>
                  <p>
                    <strong>5.4</strong> En cas d'accès non autorisé ou de faille de sécurité, le Client s'engage à en informer immédiatement Reversed Sàrl.
                  </p>
                </div>

                <Separator />

                {/* Article 6 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">6. Disponibilité et maintenance</h2>
                  <p className="mb-2">
                    <strong>6.1</strong> Reversed Sàrl s'efforce d'assurer une disponibilité élevée de Piktable (objectif : 99 % hors maintenance planifiée).
                  </p>
                  <p className="mb-2">
                    <strong>6.2</strong> Les opérations de maintenance peuvent entraîner des interruptions temporaires du service, annoncées préalablement dans la mesure du possible.
                  </p>
                  <p>
                    <strong>6.3</strong> Reversed Sàrl ne peut être tenue responsable des interruptions dues à des causes externes (panne Internet, hébergeur, force majeure, etc.).
                  </p>
                </div>

                <Separator />

                {/* Article 7 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">7. Propriété intellectuelle</h2>
                  <p className="mb-2">
                    <strong>7.1</strong> L'ensemble du code, des interfaces, textes, graphismes et éléments de la Solution reste la propriété exclusive de Reversed Sàrl.
                  </p>
                  <p className="mb-2">
                    <strong>7.2</strong> Le Client obtient uniquement un droit d'utilisation de la Solution pendant la durée du contrat, sans transfert de propriété intellectuelle.
                  </p>
                  <p>
                    <strong>7.3</strong> Toute tentative de copie, reproduction ou ingénierie inverse est strictement interdite.
                  </p>
                </div>

                <Separator />

                {/* Article 8 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">8. Données et confidentialité</h2>
                  <p>
                    La collecte, l'hébergement et le traitement des données personnelles dans le cadre de la Solution sont régis par la Politique de gestion des données personnelles ci-après, qui fait partie intégrante des présentes Conditions.
                  </p>
                </div>

                <Separator />

                {/* Article 9 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">9. Responsabilité</h2>
                  <p className="mb-2">
                    <strong>9.1</strong> Reversed Sàrl répond uniquement des dommages directs résultant d'une faute grave ou intentionnelle.
                  </p>
                  <p className="mb-2">
                    <strong>9.2</strong> En tout état de cause, la responsabilité totale de Reversed Sàrl est limitée au montant total payé par le Client au cours des 12 derniers mois.
                  </p>
                  <p>
                    <strong>9.3</strong> Reversed Sàrl ne saurait être tenue responsable des dommages indirects tels que perte de chiffre d'affaires, perte de clientèle, ou interruption d'activité.
                  </p>
                </div>

                <Separator />

                {/* Article 10 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">10. Modification des Conditions</h2>
                  <p className="mb-2">
                    <strong>10.1</strong> Reversed Sàrl peut modifier les présentes Conditions avec un préavis écrit de 30 jours.
                  </p>
                  <p className="mb-2">
                    <strong>10.2</strong> L'utilisation continue de Piktable après la date d'entrée en vigueur des modifications vaut acceptation tacite.
                  </p>
                  <p>
                    <strong>10.3</strong> En cas de désaccord, le Client peut résilier son contrat avant la date d'application.
                  </p>
                </div>

                <Separator />

                {/* Article 11 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">11. Loi applicable et juridiction</h2>
                  <p className="mb-2">
                    <strong>11.1</strong> Les présentes Conditions sont soumises au droit suisse.
                  </p>
                  <p>
                    <strong>11.2</strong> En cas de litige, les tribunaux du canton de Vaud (Suisse) sont seuls compétents, sous réserve d'un for impératif différent.
                  </p>
                </div>

                <Separator />

                {/* Article 12 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">12. Dispositions finales</h2>
                  <p className="mb-2">
                    <strong>12.1</strong> Si une disposition est déclarée invalide, les autres demeurent applicables.
                  </p>
                  <p>
                    <strong>12.2</strong> Les titres des articles ne modifient pas l'interprétation des clauses.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Privacy Policy Section */}
        <section id="privacy" className="mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl font-playfair">
                Politique de gestion des données personnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <div className="space-y-6">
                {/* Section 1 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">1. Responsable du traitement</h2>
                  <Card className="bg-muted/50 border-primary/20">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Reversed Sàrl</p>
                      <p className="mb-1">Chemin des Fleurettes 22</p>
                      <p className="mb-3">1007 Lausanne – Suisse</p>
                      <a 
                        href="mailto:hello@reversed.cc" 
                        className="inline-flex items-center gap-2 text-primary hover:underline"
                      >
                        <Mail className="w-4 h-4" />
                        hello@reversed.cc
                      </a>
                    </CardContent>
                  </Card>
                </div>

                <Separator />

                {/* Section 2 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">2. Finalités du traitement</h2>
                  <p className="mb-3">Les données collectées via Piktable sont utilisées exclusivement pour :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>La gestion des réservations (créneaux, tables, notes, intolérances, e-mails automatiques).</li>
                    <li>L'accès et la gestion du tableau de bord staff.</li>
                    <li>La facturation, le support client et la relation contractuelle.</li>
                    <li>L'amélioration du service, les statistiques internes et la sécurité du système.</li>
                  </ul>
                </div>

                <Separator />

                {/* Section 3 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">3. Base légale</h2>
                  <p className="mb-3">Les traitements reposent sur :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>L'exécution du contrat entre Reversed Sàrl et le Client.</li>
                    <li>L'intérêt légitime du Responsable (sécurité, maintenance, amélioration du service).</li>
                    <li>Le consentement explicite lorsque requis.</li>
                  </ul>
                  <p className="mt-3">
                    Tous les traitements respectent la Loi fédérale sur la protection des données (LPD / FADP).
                  </p>
                </div>

                <Separator />

                {/* Section 4 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">4. Types de données collectées</h2>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Données d'identification :</strong> nom, prénom, e-mail, nom du restaurant, adresse.</li>
                    <li><strong>Données de réservation :</strong> date, heure, nombre de personnes, notes, intolérances.</li>
                    <li><strong>Données techniques :</strong> adresse IP, navigateur, logs, comportement d'utilisation.</li>
                    <li><strong>Données de facturation :</strong> coordonnées de facturation, historique de paiements.</li>
                  </ul>
                </div>

                <Separator />

                {/* Section 5 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">5. Destinataires et transferts</h2>
                  <p className="mb-2">
                    Les données sont traitées par Reversed Sàrl et ses prestataires techniques (hébergeurs, outils e-mailing, etc.).
                  </p>
                  <p className="mb-2">
                    L'hébergement est assuré sur des serveurs sécurisés, situés en Suisse ou dans l'Union européenne.
                  </p>
                  <p>
                    Des transferts vers des pays hors Suisse ou UE ne sont effectués que si des garanties appropriées sont en place (clauses contractuelles types ou consentement explicite de l'utilisateur).
                  </p>
                </div>

                <Separator />

                {/* Section 6 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">6. Durée de conservation</h2>
                  <p className="mb-2">
                    Les données sont conservées pendant la durée du contrat et jusqu'à 5 ans après sa fin pour des raisons légales (comptabilité, litiges potentiels).
                  </p>
                  <p>
                    Passé ce délai, elles sont anonymisées ou supprimées.
                  </p>
                </div>

                <Separator />

                {/* Section 7 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">7. Sécurité</h2>
                  <p className="mb-3">
                    Reversed Sàrl applique des mesures techniques et organisationnelles appropriées pour protéger les données :
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>chiffrement des communications (HTTPS),</li>
                    <li>contrôle d'accès restreint,</li>
                    <li>sauvegardes régulières,</li>
                    <li>surveillance et correction proactive des vulnérabilités.</li>
                  </ul>
                </div>

                <Separator />

                {/* Section 8 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">8. Droits des personnes concernées</h2>
                  <p className="mb-3">Toute personne concernée peut, à tout moment :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>accéder à ses données,</li>
                    <li>demander leur rectification ou suppression,</li>
                    <li>s'opposer à leur traitement,</li>
                    <li>demander la portabilité des données (dans les limites techniques).</li>
                  </ul>
                  <p className="mt-3">
                    Les demandes doivent être adressées à : <a href="mailto:hello@reversed.cc" className="text-primary hover:underline">hello@reversed.cc</a>
                  </p>
                  <p className="mt-2">
                    Reversed Sàrl s'engage à y répondre dans un délai raisonnable.
                  </p>
                </div>

                <Separator />

                {/* Section 9 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">9. Cookies et technologies similaires</h2>
                  <p className="mb-2">
                    Le site utilise des cookies strictement nécessaires au fonctionnement et à la mesure anonyme de fréquentation.
                  </p>
                  <p>
                    L'utilisateur peut à tout moment refuser ou configurer l'usage des cookies via le bandeau d'information affiché sur le site.
                  </p>
                </div>

                <Separator />

                {/* Section 10 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">10. Modifications</h2>
                  <p className="mb-2">
                    Cette politique peut être mise à jour pour refléter l'évolution de la Solution ou des lois applicables.
                  </p>
                  <p>
                    La version en vigueur est toujours consultable sur le site Piktable.ch avec la date de mise à jour affichée.
                  </p>
                </div>

                <Separator />

                {/* Section 11 */}
                <div>
                  <h2 className="text-xl font-semibold mb-3">11. Contact</h2>
                  <p className="mb-3">Pour toute question relative à la protection des données :</p>
                  <Card className="bg-muted/50 border-primary/20">
                    <CardContent className="pt-6">
                      <p className="font-semibold mb-2">Reversed Sàrl</p>
                      <p className="mb-1">Chemin des Fleurettes 22, 1007 Lausanne – Suisse</p>
                      <a 
                        href="mailto:hello@reversed.cc" 
                        className="inline-flex items-center gap-2 text-primary hover:underline"
                      >
                        <Mail className="w-4 h-4" />
                        hello@reversed.cc
                      </a>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Last update notice */}
        <div className="text-center text-sm text-muted-foreground">
          Dernière mise à jour : 10 novembre 2025
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default Legal;
